import React from "react";
import MainInput from "components/MainApp/atoms/Forms/MainInput";
import MainTextArea from "components/MainApp/atoms/Forms/MainTextArea";
import MultiSelectInput from "components/MainApp/atoms/Forms/MultiSelectInput";

import Checkbox from "components/Auth/atoms/forms/CheckboxCostum";
import permisos, { specificScopes } from "./permisos";

import styles from "./RoleForm.module.scss";
import i18n from "i18n";
import { capitalize } from "helpers/utils/utils_general";

const RoleForm = ({
  role,
  handleSelect,
  handleSelectMobile,
  handleChange,
  handleClose,
  handleOpen,
  handleSubmit,
  errors,
  continueEnabled,
}) => {
  const selected = [];

  const mapPermissions = (permissions, initialValue) => {
    return permissions.map(permission => {
      let value = initialValue;
      if (initialValue === i18n.t("permission.selected")) return { id: permission, value };

      if (permission.includes("read_")) {
        value = i18n.t("permission.view");
      } else if (permission.includes("create_")) {
        value = i18n.t("permission.create");
      } else if (permission.includes("update_")) {
        value = i18n.t("permission.update");
      } else if (permission.includes("delete_")) {
        value = i18n.t("permission.delete");
      }
      return { id: permission, value };
    });
  }

  /**
   * Treatment special for roles with multiple permissions in mobile
   *
   * @param {*} scopeName name of the scope
   * @param {*} permissions permissions of the role
   * @param {*} perm permissions list (permisos | specificScopes)
   * @returns
   */
  const multiSelectPermissions = (scopeName, permissions, perm, initialValue = "") => {
    // si no es mobile devolvemos los permisos sin tratar
    if (!handleSelectMobile) return permissions;
    if (!permissions || permissions.length === 0) return [];

    const scope = perm?.find(p => p.name === scopeName);
    if (scope) {
      // si es initialValue "" (permisos) filtramos los permisos que no sean de los scopes especificos
      if (initialValue === "") {
        permissions = permissions.filter(permission => !specificScopes.some(s => permission.includes(s.id)));
      }
      if (permissions.some(permission => permission.includes(scope.id))) {
        permissions = permissions.filter(permission => permission.includes(scope.id));
        return mapPermissions(permissions, initialValue);
      } else {
        return [];
      }
    } else {
      return permissions;
    }
  }

  const renderMultiSelect = (scope) => {
    const items = [
      {
        id: `read_${scope.id}`,
        value: i18n.t("permission.view"),
      },
      {
        id: `create_${scope.id}`,
        value: i18n.t("permission.create"),
      },
      {
        id: `update_${scope.id}`,
        value: i18n.t("permission.update"),
      },
      {
        id: `delete_${scope.id}`,
        value: i18n.t("permission.delete"),
      },
    ];
    return (
      <div
        className={styles["role-form-field"]}
        data-automation-id={`input-scope-${scope.id}`}
        key={`input-scope-${scope.id}`}
      >
        <MultiSelectInput
          items={items.filter(i => {
            return !['read_business', 'create_business', 'create_stock',
              'delete_stock', 'delete_orders'].includes(i.id)
          })}
          selected={multiSelectPermissions(scope.name, role.permissions, permisos)}
          label={scope.name}
          handleChange={handleSelect}
          handleChangeMobile={handleSelectMobile}
          handleOpen={handleOpen}
        ></MultiSelectInput>
      </div>
    );
  };

  const renderSpecificMultiSelect = (scope) => {
    const items = [
      {
        id: `${scope.id}`,
        value: i18n.t("permission.selected"),
      },
    ];
    return (
      <div
        className={styles["role-form-field"]}
        data-automation-id={`input-scope-${scope.id}`}
        key={`input-scope-${scope.id}`}
      >
        <MultiSelectInput
          items={items}
          selected={multiSelectPermissions(scope.name, role.permissions, specificScopes, i18n.t("permission.selected"))}
          label={scope.name}
          handleChange={handleSelect}
          handleChangeMobile={handleSelectMobile}
          handleOpen={handleOpen}
        ></MultiSelectInput>
      </div>
    );
  };

  return (
    <div className={styles["role-form-content"]}>
      <h1 className={styles["role-form-title"]}>{i18n.t("enter.data")}</h1>
      <div className={styles["role-form-field"]} data-automation-id="input-name">
        <MainInput
          label={i18n.t("rol.name")}
          required={true}
          id={"role-name"}
          name="name"
          value={role ? role.name : ""}
          handleChange={handleChange}
          maxLength={100}
          error={errors.name && errors.name}
        />
      </div>
      <div className={styles["role-form-field"]} data-automation-id="input-description">
        <MainTextArea
          label={capitalize(i18n.t("description"))}
          required={true}
          id={"role-description"}
          name="description"
          value={role ? role.description : ""}
          handleChange={handleChange}
          maxLength={100}
          error={errors.description && errors.description}
        />
      </div>
      <h1 className={styles["role-form-title"]}>{i18n.t("define.permission.levels")} *</h1>

      <table className={styles["permisos"]}>
        <thead>
          <tr>
            <th className={styles["scope-th"]}>{i18n.t("general.scopes")}</th>
            <th>{i18n.t("read").toUpperCase()}</th>
            <th>{i18n.t("permission.create").toUpperCase()}</th>
            <th>{i18n.t("permission.update").toUpperCase()}</th>
            <th>{i18n.t("permission.delete").toUpperCase()}</th>
          </tr>
        </thead>
        <tbody>
          {permisos.map((permiso) => {
            return (
              <tr key={permiso.id} className={styles["permiso-wrapper"]}>
                <td className={styles["left"]}>
                  <p className={styles["title"]}>{permiso.name}</p>
                </td>
                <td className={styles["option"]}>
                  <Checkbox
                    handleChange={() => handleSelect(`read_${permiso.id}`)}
                    checked={role.permissions.includes(`read_${permiso.id}`)}
                    disabled={['business'].includes(permiso.id)}
                  ></Checkbox>
                </td>
                <td className={styles["option"]}>
                  <Checkbox
                    handleChange={() => handleSelect(`create_${permiso.id}`)}
                    checked={role.permissions.includes(`create_${permiso.id}`)}
                    disabled={['business', 'stock'].includes(permiso.id)}
                  ></Checkbox>
                </td>
                <td className={styles["option"]}>
                  <Checkbox
                    handleChange={() => handleSelect(`update_${permiso.id}`)}
                    checked={role.permissions.includes(`update_${permiso.id}`)}
                  ></Checkbox>
                </td>
                <td className={styles["option"]}>
                  <Checkbox
                    handleChange={() => handleSelect(`delete_${permiso.id}`)}
                    checked={role.permissions.includes(`delete_${permiso.id}`)}
                    disabled={['stock', 'orders'].includes(permiso.id)}
                  ></Checkbox>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <table className={styles["permisos"]}>
        <thead>
          <tr>
            <th className={styles["scope-th"]}>{i18n.t("special.scopes")}</th>
            <th>{i18n.t("permission.selected").toUpperCase()}</th>
          </tr>
        </thead>
        <tbody>
          {specificScopes.map((permiso) => {
            return (
              <tr key={permiso.id} className={styles["permiso-wrapper"]}>
                <td className={styles["left"]}>
                  <p className={styles["title"]}>{permiso.name}</p>
                </td>
                <td className={styles["option"]}>
                  <Checkbox
                    handleChange={() => handleSelect(`${permiso.id}`)}
                    checked={role.permissions.includes(`${permiso.id}`)}
                  ></Checkbox>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>


      <div className={styles["permisos-mobile"]}>
        {permisos.map((permiso) => {
          return renderMultiSelect(permiso)
        })}

        {specificScopes.map((permiso) => {
          return renderSpecificMultiSelect(permiso)
        })}
      </div>
      <div className={styles["role-form-submit"]}>
        <div
          className={styles["role-form-submit-button"] + (continueEnabled ? ' ' + styles["enabled"] : "")}
          onClick={continueEnabled ? (e) => handleSubmit(e) : null}
        >
          {role.id ? i18n.t("save").toUpperCase() : i18n.t("create").toUpperCase()}
        </div>
      </div>
    </div>
  );
};

export default RoleForm;
